import {clearToken, getToken} from '@/common/utils/token'
import JSONBig from 'json-bigint'
import {isHttp} from '@/common/utils/common'
import {UserTypeEnum} from "@/common/enums/system";

export type PageRequest = {
    pageNumber: number | 1
    pageSize: number | 10
}

export type PageResponse<T = any> = {
    records: T[]
    totalSize: number | 0
}

export type ResultResponse<T = any> = {
    code: string
    message: string
    success: boolean | false
    tip: boolean | false
    traceId: string
    data: T
    timestamp: number | 0
}

export const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

export const allowUrlList: string[] = ['/system/employee/token/get']

export const interceptorRequest = () => {
    uni.addInterceptor('request', {
        invoke(request) {
            // console.log(request)
            if (allowUrlList.includes(request.url as unknown as string)) {
                return request
            }
            if (VITE_API_BASE_URL && !isHttp(request.url)) {
                request.url = `${VITE_API_BASE_URL}${request.url}`
            }
            let contentType = undefined
            let ut = undefined
            if (request.header) {
                contentType = request.header['Content-Type'];
                ut = request.header['ut'];
            } else {
                request.header = {}
            }
            if (!contentType || contentType === '') {
                request.header['Content-Type'] = 'application/json; charset=utf-8'
            }
            if (!ut || ut === '') {
                request.header['ut'] = UserTypeEnum.CUSTOMER
            }
            request.dataType = 'text'
            const token = getToken()
            request.header['Authorization'] = `Bearer ${token}`
        },
    })
}

export type RequestOptions = {
    /**
     * 资源url
     */
    url?: string;
    /**
     * 请求的参数
     */
    data?: string | AnyObject | ArrayBuffer;
    /**
     * 设置请求的 header，header 中不能设置 Referer。
     */
    header?: any;
    /**
     * 默认为 GET
     * 可以是：OPTIONS，GET，HEAD，POST，PUT，DELETE，TRACE，CONNECT
     */
    method?: 'OPTIONS' | 'GET' | 'HEAD' | 'POST' | 'PUT' | 'DELETE' | 'TRACE' | 'CONNECT';
    /**
     * 超时时间
     */
    timeout?: number;
    /**
     * 如果设为json，会尝试对返回的数据做一次 JSON.parse
     */
    dataType?: string;
    /**
     * 设置响应的数据类型。合法值：text、arraybuffer
     */
    responseType?: string;
    /**
     * 验证 ssl 证书
     */
    sslVerify?: boolean;
    /**
     * 跨域请求时是否携带凭证
     */
    withCredentials?: boolean;
    /**
     * DNS解析时优先使用 ipv4
     */
    firstIpv4?: boolean;
    /**
     * 开启 http2
     */
    enableHttp2?: boolean;
    /**
     * 开启 quic
     */
    enableQuic?: boolean;
    /**
     * 开启 cache
     */
    enableCache?: boolean;
    /**
     * 是否开启 HttpDNS 服务。如开启，需要同时填入 httpDNSServiceId 。 HttpDNS 用法详见 [移动解析HttpDNS](https://developers.weixin.qq.com/miniprogram/dev/framework/ability/HTTPDNS.html)
     */
    enableHttpDNS?: boolean;
    /**
     * HttpDNS 服务商 Id。 HttpDNS 用法详见 [移动解析HttpDNS](https://developers.weixin.qq.com/miniprogram/dev/framework/ability/HTTPDNS.html)
     */
    httpDNSServiceId?: boolean;
    /**
     * 开启 transfer-encoding chunked
     */
    enableChunked?: boolean;
    /**
     * wifi下使用移动网络发送请求
     */
    forceCellularNetwork?: boolean;
    /**
     * 默认 false，开启后可在headers中编辑cookie（支付宝小程序10.2.33版本开始支持）
     */
    enableCookie?: boolean;
    /**
     * 是否开启云加速（详见[云加速服务](https://smartprogram.baidu.com/docs/develop/extended/component-codeless/cloud-speed/introduction/)）
     */
    cloudCache?: object | boolean;
    /**
     * 控制当前请求是否延时至首屏内容渲染后发送
     */
    defer?: boolean;
    success?: (result: UniNamespace.RequestSuccessCallbackResult) => void;
    /**
     * 失败的回调函数
     */
    fail?: (result: UniNamespace.GeneralCallbackResult) => void;
    /**
     * 结束的回调函数（调用成功、失败都会执行）
     */
    complete?: (result: UniNamespace.GeneralCallbackResult) => void;
}

class HttpUtil {

    static post = async <R = ResultResponse | any, D = any>(
        url: string,
        request?: D,
        options?: RequestOptions,
    ): Promise<R> => {
        options = {
            url: url,
            method: 'POST',
            data: request || {},
            dataType: 'text', ...options,
        }

        if (!allowUrlList.includes(options.url as unknown as string)) {
            if (VITE_API_BASE_URL && !isHttp(options.url)) {
                options.url = `${VITE_API_BASE_URL}${options.url}`
            }
            let contentType = undefined
            if (options.header) {
                contentType = options.header['Content-Type'];
            } else {
                options.header = {}
            }
            if (!contentType || contentType === '') {
                options.header['Content-Type'] = 'application/json; charset=utf-8'
            }
            options.dataType = 'text'
            const token = getToken()
            options.header['Authorization'] = `Bearer ${token}`
        }
        return uni.request(options as UniNamespace.RequestOptions)
            .then(response => {
                if (!response) {
                    uni.showToast({
                        title: '响应数据异常',
                        icon: 'none',
                    })
                    return Promise.reject(new Error('响应数据异常'))
                }
                let data = response.data
                if (typeof data !== 'string') {
                    uni.showToast({
                        title: '响应数据异常',
                        icon: 'none',
                    })
                    return Promise.reject(new Error('响应数据异常'))
                }
                const JSONBigIntNative = JSONBig()
                let result = JSON.parse(JSON.stringify(JSONBigIntNative.parse(data)))
                // console.log(result)
                if (!result?.success) {
                    // token 过期
                    if (result.code === 'R_0010000011' || result.code === 'R_0010000012') {
                        clearToken()
                        // const commonStore = useCommonStore()
                        // const dictionaryStore = useDictionaryStore()
                        // const userStore = useUserStore()
                        // commonStore.reset()
                        // dictionaryStore.reset()
                        // userStore.reset()
                        uni.removeStorageSync('common')
                        uni.removeStorageSync('dictionary')
                        uni.removeStorageSync('user')
                        uni.navigateTo({url: '/pages/pages/customer/customer/login'})
                    }
                }

                if (result?.success) {
                    if (result?.tip) {
                        uni.showToast({
                            title: result?.message,
                            icon: 'none',
                        })
                    }
                } else {
                    if (result?.tip) {
                        uni.showToast({
                            title: result?.message,
                            icon: 'none',
                        })
                    }
                }
                return Promise.resolve(result)
            })
            .catch((error) => {
                console.error(error)
                uni.showToast({title: '网络异常，请检查网络后重试~', icon: 'none'})
                return Promise.reject(error)
            })
            .finally(() => {
            })
    }
}

export default HttpUtil
